import { apiClient } from "./client";
import type {
  Case,
  CaseInboxResponse,
  CaseFilters,
  CreateCaseRequest,
  UpdateCaseStateRequest,
  DashboardMetrics,
  CaseComment,
  CreateCommentRequest,
  CaseAttachment,
  CaseActionLog,
  AssignUserRequest,
  CreateNodeResponsibilityRequest,
  BulkAssignmentRequest,
  ResponsibilityMatrixRow,
  EligibleUsersResponse,
  ReEvaluateResponse,
  CaseDocumentsResponse,
} from "../types/case";
import {
  transformCaseInboxResponse,
  transformApiCase,
} from "../utils/caseDataTransformer";
import { getAuthHeaders } from "../utils/authStorage";

// Case Management API endpoints based on AlphaCore API structure
const CASE_BASE_URL = "/case-management/cases";

export class CasesApi {
  /**
   * Get cases for inbox with filtering and pagination
   * Maps to: GET /api/case-management/cases
   */
  async getInbox(filters: CaseFilters = {}): Promise<CaseInboxResponse> {
    const params = new URLSearchParams();

    // Add pagination parameters
    params.append("page", (filters.pageNumber || 1).toString());
    params.append("pageSize", (filters.pageSize || 20).toString());

    // Add other filter parameters according to AlphaCore API specification
    if (filters.states?.length) {
      params.append("caseStatuses", filters.states.join(","));
    }
    if (filters.priorities?.length) {
      filters.priorities.forEach((priority) =>
        params.append("priorities", priority),
      );
    }
    if (filters.complianceNodeIds?.length) {
      filters.complianceNodeIds.forEach((id) =>
        params.append("complianceNodeIds", id),
      );
    }
    if (filters.assignedUserIds?.length) {
      filters.assignedUserIds.forEach((id) =>
        params.append("assignedUserIds", id),
      );
    }
    // Date filters per API spec (CASE_LIST_SEARCH_GUIDE.md)
    if (filters.dueDateFrom) {
      params.append("fromDate", filters.dueDateFrom);
    }
    if (filters.dueDateTo) {
      params.append("toDate", filters.dueDateTo);
    }
    if (filters.createdDateFrom)
      params.append("createdDateFrom", filters.createdDateFrom);
    if (filters.createdDateTo)
      params.append("createdDateTo", filters.createdDateTo);
    if (filters.searchText) params.append("searchText", filters.searchText);
    if (filters.creationType)
      params.append("creationType", filters.creationType); // ALH-194
    if (filters.sortBy) params.append("sortBy", filters.sortBy);
    if (filters.sortDirection)
      params.append("sortDirection", filters.sortDirection);

    // Use the working endpoint: /api/case-management/cases instead of /cases/inbox
    // ApiClient returns: { data: T, success: boolean, message?: string, errors?: string[] }
    const response = await apiClient.get<{
      data: Record<string, unknown>[];
      page: number;
      pageSize: number;
      totalElements: number;
      totalPages: number;
    }>(`${CASE_BASE_URL}?${params}`);

    // Check if we have a successful response
    if (response.success === false) {
      throw new Error(response.message || "Failed to fetch cases");
    }

    const casesData = response.data?.data || [];

    // Transform the response to match expected structure
    const transformedResponse = transformCaseInboxResponse(casesData);

    // Add pagination metadata from the new response structure
    return {
      ...transformedResponse,
      totalCount: response.data?.totalElements || 0,
      pageNumber: response.data?.page || 1,
      pageSize: response.data?.pageSize || 20,
      totalPages: response.data?.totalPages || 0,
      hasNextPage:
        (response.data?.page || 0) < (response.data?.totalPages || 0),
      hasPreviousPage: (response.data?.page || 0) > 1,
    };
  }

  /**
   * Get a specific case by ID with attachments (RECOMMENDED APPROACH)
   * Maps to: GET /api/case-management/cases/{id}?includeAttachments=true&includeFiles=true&includeGroupedAttachments=true
   */
  async getById(id: number): Promise<Case> {
    // Use the recommended approach from the documentation
    const response = await apiClient.get<Case>(
      `${CASE_BASE_URL}/${id}?includeAttachments=true&includeFiles=true&includeGroupedAttachments=true`,
    );

    // Transform the API response to handle numeric state values
    return transformApiCase(response.data);
  }

  /**
   * Create a new case manually
   * Maps to: POST /api/case-management/cases
   */
  async create(caseData: CreateCaseRequest): Promise<Case> {
    const response = await apiClient.post<Case>(CASE_BASE_URL, caseData);
    return response.data;
  }

  /**
   * Update case status/state
   * Maps to: PUT /api/case-management/cases/{id}/status
   */
  async updateStatus(
    id: number,
    stateUpdate: UpdateCaseStateRequest,
  ): Promise<Case> {
    const response = await apiClient.put<Case>(
      `${CASE_BASE_URL}/${id}/status`,
      stateUpdate,
    );
    return response.data;
  }

  /**
   * Delete a case
   * Maps to: DELETE /api/case-management/cases/{id}
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`${CASE_BASE_URL}/${id}`);
  }

  /**
   * Assign user to case
   * Maps to: POST /api/case-management/cases/{id}/assign
   */
  async assignUser(
    caseId: number,
    assignment: AssignUserRequest,
  ): Promise<Case> {
    const response = await apiClient.post<Case>(
      `${CASE_BASE_URL}/${caseId}/assign`,
      assignment,
    );
    return response.data;
  }

  /**
   * Unassign user from case
   * Maps to: DELETE /api/case-management/cases/{id}/assign/{userId}
   */
  async unassignUser(caseId: number, userId: string): Promise<Case> {
    const response = await apiClient.delete<Case>(
      `${CASE_BASE_URL}/${caseId}/assign/${userId}`,
    );
    return response.data;
  }

  /**
   * Get eligible users for case assignment
   * Maps to: GET /api/case-management/cases/{id}/eligible-users
   */
  async getEligibleUsers(caseId: number): Promise<EligibleUsersResponse> {
    const response = await apiClient.get<EligibleUsersResponse>(
      `${CASE_BASE_URL}/${caseId}/eligible-users`,
    );
    return response.data;
  }

  /**
   * Escalate a case
   * Maps to: POST /api/case-management/cases/{id}/escalate
   */
  async escalate(caseId: number, reason?: string): Promise<Case> {
    const response = await apiClient.post<Case>(
      `${CASE_BASE_URL}/${caseId}/escalate`,
      { reason },
    );
    return response.data;
  }

  /**
   * Reopen a case
   * Maps to: POST /api/case-management/cases/{id}/reopen
   */
  async reopen(caseId: number, reason?: string): Promise<Case> {
    const response = await apiClient.post<Case>(
      `${CASE_BASE_URL}/${caseId}/reopen`,
      { reason },
    );
    return response.data;
  }

  // Comments API
  /**
   * Get comments for a case
   * Maps to: GET /api/case-management/cases/{id}/comments
   */
  async getComments(caseId: number): Promise<CaseComment[]> {
    const response = await apiClient.get<CaseComment[]>(
      `${CASE_BASE_URL}/${caseId}/comments`,
    );
    return response.data;
  }

  /**
   * Add a comment to a case
   * Maps to: POST /api/case-management/cases/{id}/comments
   */
  async addComment(
    caseId: number,
    comment: CreateCommentRequest,
  ): Promise<CaseComment> {
    const response = await apiClient.post<CaseComment>(
      `${CASE_BASE_URL}/${caseId}/comments`,
      comment,
    );
    return response.data;
  }

  /**
   * Update a comment
   * Maps to: PUT /api/case-management/cases/{caseId}/comments/{commentId}
   */
  async updateComment(
    caseId: number,
    commentId: number,
    commentText: string,
  ): Promise<CaseComment> {
    const response = await apiClient.put<CaseComment>(
      `${CASE_BASE_URL}/${caseId}/comments/${commentId}`,
      { commentText },
    );
    return response.data;
  }

  /**
   * Delete a comment
   * Maps to: DELETE /api/case-management/cases/{caseId}/comments/{commentId}
   */
  async deleteComment(caseId: number, commentId: number): Promise<void> {
    await apiClient.delete(`${CASE_BASE_URL}/${caseId}/comments/${commentId}`);
  }

  // Attachments API
  /**
   * Get attachments for a case
   * Maps to: GET /api/case-management/cases/{id}/attachments
   */
  async getAttachments(caseId: number): Promise<CaseAttachment[]> {
    const response = await apiClient.get<CaseAttachment[]>(
      `${CASE_BASE_URL}/${caseId}/attachments`,
    );
    return response.data;
  }

  /**
   * Upload an attachment to a case
   * Maps to: POST /api/case-management/cases/{id}/attachments
   */
  async uploadAttachment(
    caseId: number,
    file: File,
    tags: string[] = [],
  ): Promise<CaseAttachment> {
    const formData = new FormData();
    formData.append("file", file);
    if (tags.length > 0) {
      formData.append("tags", JSON.stringify(tags));
    }

    const response = await apiClient.post<CaseAttachment>(
      `${CASE_BASE_URL}/${caseId}/attachments`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  }

  /**
   * Update attachment tags
   * Maps to: PUT /api/case-management/cases/{caseId}/attachments/{attachmentId}/tags
   */
  async updateAttachmentTags(
    caseId: string,
    attachmentId: string,
    tags: string[],
  ): Promise<CaseAttachment> {
    const response = await apiClient.put<CaseAttachment>(
      `${CASE_BASE_URL}/${caseId}/attachments/${attachmentId}/tags`,
      { tags },
    );
    return response.data;
  }

  /**
   * Delete an attachment
   * Maps to: DELETE /api/case-management/cases/{caseId}/attachments/{attachmentId}
   */
  async deleteAttachment(caseId: number, attachmentId: number): Promise<void> {
    await apiClient.delete(
      `${CASE_BASE_URL}/${caseId}/attachments/${attachmentId}`,
    );
  }

  /**
   * Download an attachment
   * Maps to: GET /api/case-management/cases/{caseId}/attachments/{attachmentId}/download
   */
  async downloadAttachment(
    caseId: number,
    attachmentId: string,
  ): Promise<Blob> {
    const response = await fetch(
      `${apiClient.baseURL}${CASE_BASE_URL}/${caseId}/attachments/${attachmentId}/download`,
      {
        credentials: "include",
        headers: { ...getAuthHeaders() },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to download attachment: ${response.statusText}`);
    }

    return response.blob();
  }

  // Bulk Operations
  /**
   * Bulk assign cases to users
   * Maps to: POST /api/case-management/cases/bulk/assign
   */
  async bulkAssign(caseIds: string[], userIds: string[]): Promise<Case[]> {
    const response = await apiClient.post<Case[]>(
      `${CASE_BASE_URL}/bulk/assign`,
      {
        caseIds,
        userIds,
      },
    );
    return response.data;
  }

  /**
   * Bulk update case status
   * Maps to: PUT /api/case-management/cases/bulk/status
   */
  async bulkUpdateStatus(
    caseIds: string[],
    newStatus: string,
    reason?: string,
  ): Promise<Case[]> {
    const response = await apiClient.put<Case[]>(
      `${CASE_BASE_URL}/bulk/status`,
      {
        caseIds,
        newStatus,
        reason,
      },
    );
    return response.data;
  }

  // Hierarchy and Rule Default Assignment Management
  // (Formerly: Node Responsibility Management)
  /**
   * Get compliance hierarchy
   * Maps to: GET /api/case-management/hierarchy
   */
  async getHierarchy(companyId?: string): Promise<any> {
    const params = companyId ? `?companyId=${companyId}` : "";
    const response = await apiClient.get(`/case-management/hierarchy${params}`);
    return response.data;
  }

  /**
   * Create rule default assignment
   * (Formerly: createNodeResponsibility)
   * Maps to: POST /api/rule-management/rule-default-assignments
   */
  async createNodeResponsibility(
    assignment: CreateNodeResponsibilityRequest,
  ): Promise<any> {
    const response = await apiClient.post(
      "/rule-management/rule-default-assignments",
      assignment,
    );
    return response.data;
  }

  /**
   * Update rule default assignment
   * (Formerly: updateNodeResponsibility)
   * Maps to: PUT /api/rule-management/rule-default-assignments/rule/{id}
   */
  async updateNodeResponsibility(
    id: string,
    assignment: Partial<CreateNodeResponsibilityRequest>,
  ): Promise<any> {
    const response = await apiClient.put(
      `/rule-management/rule-default-assignments/rule/${id}`,
      assignment,
    );
    return response.data;
  }

  /**
   * Delete rule default assignment
   * (Formerly: deleteNodeResponsibility)
   * Maps to: DELETE /api/rule-management/rule-default-assignments/rule/{id}
   */
  async deleteNodeResponsibility(id: string): Promise<void> {
    await apiClient.delete(
      `/rule-management/rule-default-assignments/rule/${id}`,
    );
  }

  /**
   * Get responsibility matrix
   * Maps to: GET /api/case-management/responsibility-matrix
   */
  async getResponsibilityMatrix(
    companyId?: string,
  ): Promise<ResponsibilityMatrixRow[]> {
    const params = companyId ? `?companyId=${companyId}` : "";
    const response = await apiClient.get<ResponsibilityMatrixRow[]>(
      `/case-management/responsibility-matrix${params}`,
    );
    return response.data;
  }

  /**
   * Bulk assignment of rule default assignments
   * (Formerly: bulkAssignNodeResponsibilities)
   * Maps to: POST /api/rule-management/rule-default-assignments/bulk
   */
  async bulkAssignNodeResponsibilities(
    request: BulkAssignmentRequest,
  ): Promise<any> {
    const response = await apiClient.post(
      "/rule-management/rule-default-assignments/bulk",
      request,
    );
    return response.data;
  }

  // Search and Analytics
  /**
   * Search cases with advanced filters
   * Maps to: GET /api/case-management/cases/search
   */
  async search(query: string, filters: CaseFilters = {}): Promise<Case[]> {
    const params = new URLSearchParams({ searchText: query });

    // Add filter parameters
    if (filters.states?.length) {
      params.append("caseStatuses", filters.states.join(","));
    }
    if (filters.priorities?.length) {
      filters.priorities.forEach((priority) =>
        params.append("priorities", priority),
      );
    }
    if (filters.complianceNodeIds?.length) {
      filters.complianceNodeIds.forEach((id) =>
        params.append("complianceNodeIds", id),
      );
    }
    if (filters.assignedUserIds?.length) {
      filters.assignedUserIds.forEach((id) =>
        params.append("assignedUserIds", id),
      );
    }

    const response = await apiClient.get<Case[]>(
      `${CASE_BASE_URL}/search?${params}`,
    );
    return response.data;
  }

  /**
   * Get dashboard metrics
   * Maps to: GET /api/case-management/case-inbox/summary
   */
  async getDashboardMetrics(companyId: string): Promise<DashboardMetrics> {
    const response = await apiClient.get<DashboardMetrics>(
      `/case-management/case-inbox/summary`,
    );
    return response.data;
  }

  /**
   * Get enterprise dashboard
   * Maps to: GET /api/case-management/dashboard/enterprise
   */
  async getEnterpriseDashboard(): Promise<DashboardMetrics> {
    const response = await apiClient.get<DashboardMetrics>(
      "/case-management/dashboard/enterprise",
    );
    return response.data;
  }

  /**
   * Get department dashboard
   * Maps to: GET /api/case-management/dashboard/department/{departmentId}
   */
  async getDepartmentDashboard(
    departmentId: string,
  ): Promise<DashboardMetrics> {
    const response = await apiClient.get<DashboardMetrics>(
      `/case-management/dashboard/department/${departmentId}`,
    );
    return response.data;
  }

  /**
   * Get workload analytics
   * Maps to: GET /api/case-management/analytics/workload
   */
  async getWorkloadAnalytics(companyId?: string): Promise<any> {
    const params = companyId ? `?companyId=${companyId}` : "";
    const response = await apiClient.get(
      `/case-management/analytics/workload${params}`,
    );
    return response.data;
  }

  /**
   * Get bottleneck analysis
   * Maps to: GET /api/case-management/analytics/bottlenecks
   */
  async getBottleneckAnalysis(companyId?: string): Promise<any> {
    const params = companyId ? `?companyId=${companyId}` : "";
    const response = await apiClient.get(
      `/case-management/analytics/bottlenecks${params}`,
    );
    return response.data;
  }

  /**
   * Get team workload
   * Maps to: GET /api/case-management/analytics/team-workload
   */
  async getTeamWorkload(companyId?: string): Promise<any> {
    const params = companyId ? `?companyId=${companyId}` : "";
    const response = await apiClient.get(
      `/case-management/analytics/team-workload${params}`,
    );
    return response.data;
  }

  /**
   * Get assignment report
   * Maps to: GET /api/case-management/reports/assignments
   */
  async getAssignmentReport(companyId?: string): Promise<any> {
    const params = companyId ? `?companyId=${companyId}` : "";
    const response = await apiClient.get(
      `/case-management/reports/assignments${params}`,
    );
    return response.data;
  }

  /**
   * Get risk assessment
   * Maps to: GET /api/case-management/analytics/risk-assessment
   */
  async getRiskAssessment(companyId?: string): Promise<any> {
    const params = companyId ? `?companyId=${companyId}` : "";
    const response = await apiClient.get(
      `/case-management/analytics/risk-assessment${params}`,
    );
    return response.data;
  }

  // Export and Configuration
  /**
   * Export cases to CSV/Excel
   * Maps to: GET /api/case-management/cases/export
   */
  async exportCases(
    filters: CaseFilters = {},
    format: "csv" | "excel" = "csv",
  ): Promise<Blob> {
    const params = new URLSearchParams({ format });

    // Add filter parameters
    if (filters.states?.length) {
      params.append("caseStatuses", filters.states.join(","));
    }
    if (filters.priorities?.length) {
      filters.priorities.forEach((priority) =>
        params.append("priorities", priority),
      );
    }

    const response = await fetch(
      `${apiClient.baseURL}${CASE_BASE_URL}/export?${params}`,
      {
        credentials: "include" as RequestCredentials,
        headers: { ...getAuthHeaders() },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to export cases: ${response.statusText}`);
    }

    return response.blob();
  }

  /**
   * Import cases configuration
   * Maps to: GET /api/case-management/cases/import-config
   */
  async getImportConfiguration(): Promise<any> {
    const response = await apiClient.get(`${CASE_BASE_URL}/import-config`);
    return response.data;
  }

  /**
   * Export cases configuration
   * Maps to: GET /api/case-management/cases/export-config
   */
  async getExportConfiguration(): Promise<any> {
    const response = await apiClient.get(`${CASE_BASE_URL}/export-config`);
    return response.data;
  }

  // Manage Compliance Nodes
  /**
   * Manage compliance nodes for cases
   * Maps to: GET /api/case-management/cases/manage-compliance-nodes
   */
  async manageComplianceNodes(caseId: number): Promise<any> {
    const response = await apiClient.get(
      `${CASE_BASE_URL}/${caseId}/manage-compliance-nodes`,
    );
    return response.data;
  }

  // Manage Secondary Assignments
  /**
   * Manage secondary assignments for cases
   * Maps to: GET /api/case-management/cases/manage-secondary-assignments
   */
  async manageSecondaryAssignments(caseId: number): Promise<any> {
    const response = await apiClient.get(
      `${CASE_BASE_URL}/${caseId}/manage-secondary-assignments`,
    );
    return response.data;
  }

  // Manage Attachment Tags
  /**
   * Manage attachment tags
   * Maps to: GET /api/case-management/cases/manage-attachment-tags
   */
  async manageAttachmentTags(caseId: number): Promise<any> {
    const response = await apiClient.get(
      `${CASE_BASE_URL}/${caseId}/manage-attachment-tags`,
    );
    return response.data;
  }

  async getCaseActionLog(caseId: number): Promise<CaseActionLog[]> {
    const response = await apiClient.get<CaseActionLog[]>(
      `${CASE_BASE_URL}/${caseId}/action-log`,
    );
    return response.data;
  }

  // Dashboard methods
  /**
   * Get manager dashboard metrics
   * Maps to: GET /api/case-management/dashboard/manager
   */
  async getManagerDashboard(companyId: string): Promise<DashboardMetrics> {
    const response = await apiClient.get<DashboardMetrics>(
      `/case-management/dashboard/manager`,
      {
        params: { companyId },
      },
    );
    return response.data;
  }

  /**
   * Get overdue cases for a company
   * Maps to: GET /api/case-management/cases/overdue
   */
  async getOverdueCases(companyId: string): Promise<Case[]> {
    const response = await apiClient.get<Case[]>(`${CASE_BASE_URL}/overdue`, {
      params: { companyId },
    });
    return response.data;
  }

  /**
   * Get cases due today for a company
   * Maps to: GET /api/case-management/cases/due-today
   */
  async getCasesDueToday(companyId: string): Promise<Case[]> {
    const response = await apiClient.get<Case[]>(`${CASE_BASE_URL}/due-today`, {
      params: { companyId },
    });
    return response.data;
  }

  /**
   * Get cases due this week for a company
   * Maps to: GET /api/case-management/cases/due-this-week
   */
  async getCasesDueThisWeek(companyId: string): Promise<Case[]> {
    const response = await apiClient.get<Case[]>(
      `${CASE_BASE_URL}/due-this-week`,
      {
        params: { companyId },
      },
    );
    return response.data;
  }

  /**
   * Get allowed state transitions for a case
   * Maps to: GET /api/case-management/cases/{id}/allowed-transitions
   */
  async getAllowedTransitions(caseId: number): Promise<string[]> {
    const response = await apiClient.get<string[]>(
      `${CASE_BASE_URL}/${caseId}/allowed-transitions`,
    );
    return response.data;
  }

  /**
   * Update case (general update method)
   * Maps to: PUT /api/case-management/cases/{id}
   */
  async update(id: string, updates: Partial<CreateCaseRequest>): Promise<Case> {
    const response = await apiClient.put<Case>(
      `${CASE_BASE_URL}/${id}`,
      updates,
    );
    return response.data;
  }

  /**
   * Bulk delete cases
   * Maps to: DELETE /api/case-management/cases/bulk
   */
  async bulkDelete(caseIds: number[]): Promise<void> {
    await apiClient.delete(`${CASE_BASE_URL}/bulk`, {
      body: JSON.stringify({ caseIds }),
    });
  }

  /**
   * Re-evaluate a case by merging all attachments and triggering compliance review
   * Maps to: POST /api/case-management/cases/{id}/reEvaluate
   */
  async reEvaluate(caseId: string): Promise<ReEvaluateResponse> {
    const response = await apiClient.post<ReEvaluateResponse>(
      `${CASE_BASE_URL}/${caseId}/reEvaluate`,
      {},
    );
    return response.data;
  }

  /**
   * Get documents explicitly linked to a case (from case_documents table)
   * Maps to: GET /api/case-management/cases/{id}/case-documents
   */
  async getCaseDocuments(
    caseId: number,
    params?: { page?: number; size?: number },
  ): Promise<CaseDocumentsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.size) queryParams.append("size", params.size.toString());

    const queryString = queryParams.toString();
    const url = `${CASE_BASE_URL}/${caseId}/case-documents${queryString ? `?${queryString}` : ""}`;

    const response = await apiClient.get<CaseDocumentsResponse>(url);
    return response.data;
  }
}

// Export singleton instance
export const casesApi = new CasesApi();

// Export for dependency injection in tests
export default casesApi;
